/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Mantine
import { Drawer, Box, ScrollArea } from '@mantine/core';
import {
	useForm,
	isNotEmpty,
	matches,
} from '@mantine/form';

// Styles
import classes from './AddAdditionalityProject.module.css';

// Components
import { PreviousDrawerButton } from '@components/atoms/PreviousDrawerButton';
import { DefaultModal } from '@components/molecules/DefaultModal';

// I18n
import { useTranslation } from 'react-i18next';

// Constants
import {
	REGEX_LINKEDIN,
	REGEX_VALID_NAME,
	REGEX_VALID_PARAGRAPHE_RICH_TEXT,
	REGEX_VALID_PLACE,
	REGEX_VALID_RICH_TEXT,
	REGEX_WEBSITE,
	REGEX_X,
} from '@config/constants/regex';

// Apis
import { AddAdditionalityProjectForm } from './AddMeasurabilityProjectForm';
import { useAdditionalityProjectCreate } from '@features/catalogs/additionality-projects/list/hooks/useAdditionalityProjectCreate';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const AddAdditionalityProject = (props: {
	onClick?: () => void;
	onClose: () => void;
	opened: boolean;
}) => {
	/* ******************************** HOOKS *********************************** */
	const { t } = useTranslation();

	// Validators
	const form = useForm({
		initialValues: {
			entity: '',
			supportedBy: null,
			name: '',
			countryCode: '',
			place: '',
			cover: null,
			unit: null,
			technology: '',
			technicalDocuments: null,
			twitter: '',
			linkedin: '',
			website: '',
			typeOfFinancing: 'UNLIMITED',
			financing: 0,
			dueDate: null,
			openingDate: null,
			description: '<p></p>',
		},

		validate: {
			entity: isNotEmpty(t('global.entityRequired')),
			name: (value) => {
				if (value.length == 0) {
					return t('global.nameRequired');
				}
				if (value.length == 1) {
					return t('form.error.invalidNameLength');
				}
				if (
					!REGEX_VALID_NAME.test(value) ||
					value.length > 40
				) {
					return t('form.error.invalidName');
				}
				return null;
			},

			countryCode: isNotEmpty(
				t('global.countryRequired')
			),

			place: (value) => {
				if (value.length == 0) {
					return t('global.placeRequired');
				}
				if (value.length == 1) {
					return t('form.error.invalidPlaceLength');
				}
				if (
					!REGEX_VALID_PLACE.test(value) ||
					value.length > 50
				) {
					return t('form.error.invalidPlace');
				}
				return null;
			},
			unit: isNotEmpty(t('global.unitRequired')),
			technology: isNotEmpty(
				t('global.technologyRequired')
			),
			cover: isNotEmpty(t('global.coverRequired')),
			twitter: matches(REGEX_X, 'form.error.invalidX'),
			website: matches(
				REGEX_WEBSITE,
				'form.error.invalidWebsite'
			),
			linkedin: matches(
				REGEX_LINKEDIN,
				'form.error.invalidLinkedin'
			),
			description: (value) => {
				if (!REGEX_VALID_RICH_TEXT.test(value)) {
					return t('global.descriptionRequired');
				}
				if (
					!REGEX_VALID_PARAGRAPHE_RICH_TEXT.test(value)
				) {
					return t('form.error.invalidText');
				}
				return null;
			},
			dueDate: (value, values) => {
				return values.typeOfFinancing !== 'UNLIMITED' &&
					value == null
					? t('global.dueDateRequired')
					: null;
			},
			openingDate: (value, values) => {
				return values.typeOfFinancing !== 'UNLIMITED' &&
					value == null
					? t('global.dateOfCommissioningRequired')
					: null;
			},
		},
	});
	const {
		isOpenModalCancel,
		setIsOpenModalCancel,
		handleCloseDrawerAddAdditionalityProject,
		viewport,
	} = useAdditionalityProjectCreate({
		form: form,
	});
	/* ******************************** RENDERING ******************************* */
	return (
		<Drawer.Root
			position="right"
			size="100%"
			opened={props.opened}
			onClose={() => {
				setIsOpenModalCancel(true);
			}}
		>
			<Drawer.Overlay />
			<Drawer.Content
				ref={viewport}
				className={classes.root}
			>
				<DefaultModal
					opened={isOpenModalCancel}
					onConfirm={async () => {
						handleCloseDrawerAddAdditionalityProject(
							form
						);
					}}
					onClose={() => {
						setIsOpenModalCancel(false);
					}}
					title={t('global.backToProjectDetailsSure')}
				/>
				{/** Header */}
				<Drawer.Header className={classes.header}>
					<PreviousDrawerButton
						onClick={() => {
							setIsOpenModalCancel(true);
						}}
					>
						{t('global.backToProjectDetails')}
					</PreviousDrawerButton>
				</Drawer.Header>
				{/** Body */}
				<Drawer.Body className={classes.body}>
					<Box className={classes.bodySpacer} />
					<Box className={classes.bodyContent}>
						{/** Content */}
						<ScrollArea
							viewportRef={viewport}
							className={classes.containerContent}
						>
							<AddAdditionalityProjectForm
								isPreviousMeasurabilityProjectDetails={
									true
								}
								form={form}
								onCancel={() => {
									setIsOpenModalCancel(true);
								}}
								onBack={() => {
									props.onClose();
									form.reset();
								}}
							/>
						</ScrollArea>
					</Box>
					<Box className={classes.bodySpacer} />
				</Drawer.Body>
				{/** Footer */}
				<Box className={classes.containerFooter}>
					<Box className={classes.spacerFooter} />

					<Box className={classes.spacerFooter} />
				</Box>
			</Drawer.Content>
		</Drawer.Root>
	);
};
